import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const GIFTS = {
  card: { label: "Sympathy Card", price: 3.99, emoji: "🕊", blurb: "An animated condolence card with a personal note." },
  flowers: { label: "Virtual Bouquet", price: 5.99, emoji: "💐", blurb: "A blooming animated bouquet — beautiful on mobile." },
};

// Delivery modes:
//   email — we email the gift link directly to the recipient
//   link  — we give YOU a copyable link; you share it however you want (text, WhatsApp, etc)
export default function GiftSendModal({ open, onClose, obituaryId, recipientHint }) {
  const [giftType, setGiftType] = useState("card");
  const [recipientName, setRecipientName] = useState(recipientHint?.name || "");
  const [recipientEmail, setRecipientEmail] = useState(recipientHint?.email || "");
  const [senderName, setSenderName] = useState("");
  const [message, setMessage] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("email");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const send = async () => {
    if (!recipientName.trim() || !senderName.trim()) return toast.error("Please add both names.");
    if (deliveryMethod === "email" && !recipientEmail) return toast.error("Email is required for email delivery.");
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", {
        gift_type: giftType,
        obituary_id: obituaryId || null,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail || null,
        sender_name: senderName.trim(),
        message: message.trim(),
        delivery_method: deliveryMethod,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.used_bundle_credit && data.view_url) {
        // Bundle credit auto-applied — copy view URL straight away
        navigator.clipboard?.writeText(data.view_url).catch(() => {});
        toast.success("Gift created with your bundle credit. Link copied to clipboard.");
        onClose();
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout.");
    } finally { setBusy(false); }
  };

  return (
    <div role="dialog" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-card" style={{ width: "100%", maxWidth: 520, padding: 28, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div className="serif" style={{ fontSize: 24, color: "#fff" }}>Send a virtual gift</div>
          <button onClick={onClose} className="btn-ghost" style={{ padding: "4px 10px" }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
          {Object.entries(GIFTS).map(([id, g]) => (
            <button
              key={id}
              data-testid={`gift-pick-${id}`}
              onClick={() => setGiftType(id)}
              style={{
                padding: 16, borderRadius: 12, cursor: "pointer",
                background: giftType === id ? "rgba(212,175,55,0.12)" : "#0a0a0b",
                border: "1px solid " + (giftType === id ? "var(--gold)" : "#2a2a2a"),
                color: "#fff", textAlign: "left",
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 6 }}>{g.emoji}</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{g.label}</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{g.blurb}</div>
              <div style={{ color: "var(--gold)", fontSize: 14, marginTop: 8 }}>${g.price}</div>
            </button>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <input data-testid="gift-from" value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="From (your name)" style={{ padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          <input data-testid="gift-to" value={recipientName} onChange={e => setRecipientName(e.target.value)} placeholder="To (recipient name)" style={{ padding: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          {[
            { id: "email", label: "📧 Email it for me" },
            { id: "link", label: "🔗 Give me a link to share" },
          ].map(d => (
            <button key={d.id} data-testid={`delivery-${d.id}`} onClick={() => setDeliveryMethod(d.id)} style={{ padding: "8px 14px", borderRadius: 999, cursor: "pointer", fontSize: 13, background: deliveryMethod === d.id ? "var(--gold)" : "transparent", color: deliveryMethod === d.id ? "#0a0a0b" : "#ddd", border: "1px solid " + (deliveryMethod === d.id ? "var(--gold)" : "#2a2a2a") }}>{d.label}</button>
          ))}
        </div>

        {deliveryMethod === "email" && (
          <input data-testid="gift-recipient-email" type="email" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} placeholder="recipient@example.com" style={{ width: "100%", padding: 12, marginBottom: 10, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
        )}
        {deliveryMethod === "link" && (
          <div className="muted" style={{ fontSize: 12, marginBottom: 10, padding: 10, background: "#0a1a14", border: "1px solid #1f3a2a", borderRadius: 8, lineHeight: 1.5 }}>
            After payment we&apos;ll give you a shareable link. Copy and paste it into a text, WhatsApp, Messenger — anywhere you like.
          </div>
        )}

        <textarea data-testid="gift-message" value={message} onChange={e => setMessage(e.target.value)} rows={3} placeholder="Add a short note (optional)" style={{ width: "100%", padding: 12, marginBottom: 14, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8, resize: "vertical" }} />

        <div className="muted" style={{ fontSize: 11, marginBottom: 14, lineHeight: 1.6 }}>
          Gifts are watermarked with the recipient&apos;s name and expire 72 hours after the first view.
        </div>

        <button data-testid="gift-send-btn" onClick={send} disabled={busy} className="btn-gold" style={{ width: "100%", padding: 14 }}>
          {busy ? "Opening checkout…" : `Send ${GIFTS[giftType].label} — $${GIFTS[giftType].price}`}
        </button>
      </div>
    </div>
  );
}
